import { all, call, put, takeLatest } from 'redux-saga/effects';
import Amplify, { Auth } from 'aws-amplify';
import envConfig from '../../config';
import {
  logIn,
  ssoLogIn,
  logOut,
  restoreSession,
  resetSession,
  completeNewPassword,
  forgotPassword,
  fetchCurrentUser,
  showCognitoChallenge,
  forgotPasswordSubmit,
  setupTOTP,
  verifyTOTP,
  verifyMFA,
  getPreferredMFA,
  disableMFA,
  setupMobile,
  resendMobileVerificationCode,
  verifyMobile,
  resendMobileMFACode,
  getHelpJuiceSSOToken,
} from '../routines';
import { changeWorkSpace, getWorkSpaces } from '../routines/workspaceRoutines';
import { request, getValidToken } from '../../common/util/RestClient';
import getStorage, { LOCAL_STORAGE } from '../../common/util/StorageHelper';
import { invalidateUsersList } from '../routines/userManagementRoutines';
import { GET_USER_LOGIN_DATA } from '../../common/GoogleTagManager/GoogleTagManager';
import {
  SSO_TYPES,
  PLEASE_USE_SSO_OPTION,
  SSO_NOT_ENABLED,
} from '../../common/constants/UserManagementConstants';

const AUTH_CONFIG = 'authConfig';
const USER = 'user';
const storageType = LOCAL_STORAGE;
const CURRENT_WORK_SPACE = 'currentWorkSpace';

export function* logInSaga({ payload }) {
  try {
    const { username, password } = payload;

    const storage = getStorage(storageType);
    // Step 1
    const URL = `${envConfig.APIG_URL}tenant/${payload.username}`; // public API
    const response = yield call(request, URL, null, true);
    const { data } = response;
    // Required for step 3
    storage.setItem(
      CURRENT_WORK_SPACE,
      JSON.stringify({
        tenantId: data.tenantId,
        tenantName: null,
        isTemp: true,
      })
    );

    const gtmPayload = { ...data, username };
    GET_USER_LOGIN_DATA(gtmPayload, 'loginSaga');

    if (data.ssoType !== SSO_TYPES.NONE) {
      yield put(
        logIn.failure({
          error: { message: PLEASE_USE_SSO_OPTION },
          noAlert: true,
        })
      );
    } else {
      const authConfig = {
        region: data.region,
        userPoolId: data.userPoolId,
        userPoolWebClientId: data.userPoolWebClientId,
      };
      Amplify.configure({
        Auth: { ...authConfig, storage },
      });
      storage.setItem(AUTH_CONFIG, JSON.stringify(authConfig));

      // Step 2
      const authResponse = yield call([Auth, Auth.signIn], username, password);

      if (authResponse.challengeName) {
        yield put(
          showCognitoChallenge.trigger({
            user: authResponse,
            temporaryPassword: password,
          })
        );
      } else {
        // Step 3 : get user workspaces
        const URL2 = `${envConfig.APIG_URL}tenant/users/${encodeURIComponent(
          username
        )}`; // bff
        const workspaces = yield call(request, URL2, null);
        yield put(getWorkSpaces.success(workspaces.data));

        // if user has only one workspace, set it as the selected one
        const { tenants } = workspaces.data;
        if (tenants.length === 1) {
          yield put(changeWorkSpace.success(tenants[0]));
          storage.setItem(CURRENT_WORK_SPACE, JSON.stringify(tenants[0]));

          // Step 1 : get user details for selected workspace
          const url = `${
            envConfig.APIG_URL
          }user-management/users?username=${encodeURIComponent(username)}`;
          const userDetails = yield call(request, url, null);
          yield put(
            fetchCurrentUser.success({ user: userDetails.data.users[0] })
          );
        }
        yield put(logIn.success(authResponse));
      }
    }
  } catch (error) {
    yield put(logIn.failure({ error, noAlert: true }));
  }
}

export function* SSOLogInSaga({ payload }) {
  try {
    const { username } = payload;

    const storage = getStorage(storageType);

    // Step 1
    const URL = `${envConfig.APIG_URL}tenant/${username}`; // public API
    const response = yield call(request, URL, null, true);
    const { data } = response;

    // Required for get user workspaces
    storage.setItem(
      CURRENT_WORK_SPACE,
      JSON.stringify({ tenantId: data.tenantId, tenantName: null })
    );

    if (data.ssoType === SSO_TYPES.NONE) {
      yield put(
        logIn.failure({ error: { message: SSO_NOT_ENABLED }, noAlert: true })
      );
    } else {
      const gtmPayload = { ...data, username };
      GET_USER_LOGIN_DATA(gtmPayload, 'SSOLogInSaga');
      const authConfig = {
        region: data.region,
        userPoolId: data.userPoolId,
        userPoolWebClientId: data.userPoolWebClientId,
        oauth: {
          domain: `${envConfig.ENV}-${data.tenantIdentifier.replace(
            /_/g,
            '-'
          )}-user-pool.auth.${data.region}.amazoncognito.com`,
          scope: ['phone', 'email', 'openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: `${envConfig.WEB_APP_URL}/login`,
          redirectSignOut: `${envConfig.WEB_APP_URL}/`,
          responseType: 'code',
        },
      };
      Amplify.configure({
        Auth: { ...authConfig, storage },
      });
      storage.setItem(AUTH_CONFIG, JSON.stringify(authConfig));
      const provider = data.ssoType === SSO_TYPES.GMAIL ? 'Google' : 'Outlook';
      yield call([Auth, Auth.federatedSignIn], { provider });
    }
  } catch (error) {
    yield put(ssoLogIn.failure({ error, noAlert: true }));
  }
}

export function* resendMobileMFACodeSaga({ payload }) {
  try {
    // Initiate the singIn flow again as a workaround to resend the MFA code
    const { username, password } = payload;
    const authResponse = yield call([Auth, Auth.signIn], username, password);
    yield put(
      resendMobileMFACode.success({
        user: authResponse,
      })
    );
    const gtmPayload = {
      username,
    };
    GET_USER_LOGIN_DATA(gtmPayload, 'resendMobileMFACodeSaga');
  } catch (error) {
    yield put(resendMobileMFACode.failure({ error, noAlert: true }));
  }
}

export function* logOutSaga() {
  try {
    const response = yield call([Auth, Auth.signOut], null);
    const storage = getStorage(storageType);
    storage.removeItem(AUTH_CONFIG);
    storage.removeItem(USER);
    storage.removeItem(CURRENT_WORK_SPACE);
    yield put(logOut.success(response));
  } catch (error) {
    yield put(logOut.failure({ error }));
  }
}

export function* restoreSessionSaga() {
  try {
    const storage = getStorage(storageType);
    const authConfig = storage.getItem(AUTH_CONFIG);
    const currentWorkSpace = JSON.parse(storage.getItem(CURRENT_WORK_SPACE));
    if (authConfig && currentWorkSpace) {
      Amplify.configure({
        Auth: { ...JSON.parse(authConfig), storage },
      });
      // Step 1 : get current authenticated user object
      const authResponse = yield call(
        [Auth, Auth.currentAuthenticatedUser],
        null
      );
      if (!authResponse.signInUserSession || authResponse.challengeName) {
        yield put(restoreSession.failure({ noAlert: true }));
      } else {
        const { email } = authResponse.attributes;
        // Step 2
        const url = `${
          envConfig.APIG_URL
        }user-management/users?username=${encodeURIComponent(email)}`;
        const userDetails = yield call(request, url);
        const user = userDetails.data.users[0];
        yield put(fetchCurrentUser.success({ user }));
        yield put(changeWorkSpace.success(currentWorkSpace));
        // Step 3 : get user workspaces
        const URL2 = `${envConfig.APIG_URL}tenant/users/${encodeURIComponent(
          email
        )}`; // bff
        const workspaces = yield call(request, URL2, null);
        yield put(getWorkSpaces.success(workspaces.data));

        yield put(
          restoreSession.success({ ...authResponse, userId: user.userId })
        );
        // Step 4: get tenant details for selected workspace (tenant)
        const URL = `${envConfig.APIG_URL}tenant-information/tenants/${currentWorkSpace.tenantId}`;
        const response = yield call(request, URL, null);
        const { data } = response;
        const gtmPayload = { ...data, username: email };
        if (response.status === 'success') {
          GET_USER_LOGIN_DATA(gtmPayload, 'restoreSessionSaga');
        }
      }
    } else {
      yield put(restoreSession.failure({ noAlert: true }));
    }
  } catch (error) {
    yield put(restoreSession.failure({ error, noAlert: true }));
  }
}

export function* CompleteNewPasswordSaga({ payload }) {
  try {
    const storage = getStorage(storageType);
    const { username, password, newPassword } = payload;
    const user = yield call([Auth, Auth.signIn], username, password);
    const userId = user.challengeParam.userAttributes['custom:userId'];

    // Step 1 : update password
    const response = yield call(
      [Auth, Auth.completeNewPassword],
      user,
      newPassword,
      {}
    );
    // Step 2 : get current user details
    const URL = `${envConfig.APIG_URL}user-management/users/${userId}`;
    let userDetails = yield call(request, URL, null);
    storage.setItem(USER, JSON.stringify(userDetails.data));
    yield put(fetchCurrentUser.success({ user: userDetails.data }));

    // Step 3 : get user workspaces
    const URL2 = `${envConfig.APIG_URL}tenant/users/${encodeURIComponent(
      username
    )}`; // bff
    const workspaces = yield call(request, URL2, null);
    yield put(getWorkSpaces.success(workspaces.data));

    // if user has only one workspace, set it as the selected one
    const { tenants } = workspaces.data;
    if (tenants.length === 1) {
      yield put(changeWorkSpace.success(tenants[0]));
      storage.setItem(CURRENT_WORK_SPACE, JSON.stringify(tenants[0]));

      // Step 1 : get user details for selected workspace
      const url = `${
        envConfig.APIG_URL
      }user-management/users?username=${encodeURIComponent(username)}`;
      userDetails = yield call(request, url, null);
      yield put(fetchCurrentUser.success({ user: userDetails.data.users[0] }));
    }

    yield put(logIn.success(response));
  } catch (error) {
    yield put(completeNewPassword.failure({ error, noAlert: true }));
  }
}

export function* resetSessionSaga() {
  try {
    const storage = getStorage(storageType);
    storage.removeItem(AUTH_CONFIG);
    storage.removeItem(USER);
  } catch (error) {
    yield put(resetSession.failure({ error }));
  }
}

export function* forgotPasswordSaga({ payload }) {
  try {
    const { username } = payload;
    // step 1 : get tenant information
    const URL = `${envConfig.APIG_URL}tenant/${payload.username}`; // public API
    const response = yield call(request, URL, null, true);
    const { data } = response;
    const gtmPayload = { ...data, username };
    GET_USER_LOGIN_DATA(gtmPayload, 'forgotPasswordSaga');
    if (data.ssoType !== SSO_TYPES.NONE) {
      yield put(
        forgotPassword.failure({
          error: { message: PLEASE_USE_SSO_OPTION },
          noAlert: true,
        })
      );
    } else {
      const storage = getStorage(storageType);
      const authConfig = {
        region: data.region,
        userPoolId: data.userPoolId,
        userPoolWebClientId: data.userPoolWebClientId,
      };
      Amplify.configure({
        Auth: { ...authConfig, storage },
      });

      const response2 = yield call([Auth, Auth.forgotPassword], username);
      yield put(forgotPassword.success(response2));
    }
  } catch (error) {
    yield put(forgotPassword.failure({ error }));
  }
}

export function* forgotPasswordSubmitSaga({ payload }) {
  try {
    const { username, code, password } = payload;
    // step 1 : get tenant information
    const URL = `${envConfig.APIG_URL}tenant/${payload.username}`; // public API
    const response = yield call(request, URL, null, true);
    const { data } = response;
    const storage = getStorage(storageType);
    const authConfig = {
      region: data.region,
      userPoolId: data.userPoolId,
      userPoolWebClientId: data.userPoolWebClientId,
    };
    Amplify.configure({
      Auth: { ...authConfig, storage },
    });

    const authResponse = yield call(
      [Auth, Auth.forgotPasswordSubmit],
      username,
      code,
      password
    );
    const gtmPayload = { ...data, username };
    GET_USER_LOGIN_DATA(gtmPayload, 'forgetPassWordSubmitSaga');
    yield put(forgotPasswordSubmit.success(authResponse));
  } catch (error) {
    yield put(forgotPasswordSubmit.failure({ error }));
  }
}

export function* setupTOTPSaga() {
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );
    // Step 1.1 : issue (https://github.com/aws-amplify/amplify-js/issues/1226#issuecomment-774097887)
    try {
      yield call(
        [Auth, Auth.setPreferredMFA],
        currentAuthenticatedUser,
        'NOMFA'
      );
    } catch (e) {
      console.debug('ignore this', e);
    }

    // Step 2 : get a `authorization code` from Amazon Cognito
    const authCode = yield call(
      [Auth, Auth.setupTOTP],
      currentAuthenticatedUser
    );
    const { username } = currentAuthenticatedUser;
    const authURL = `otpauth://totp/${username}?secret=${authCode}&issuer=Velaris`;
    yield put(setupTOTP.success({ authCode, authURL }));
  } catch (error) {
    yield put(setupTOTP.failure({ error }));
  }
}

export function* verifyTOTPSaga({ payload }) {
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );
    // Step 2 : Use the generated one-time password to verify the setup
    yield call(
      [Auth, Auth.verifyTotpToken],
      currentAuthenticatedUser,
      payload.code
    );
    // Step 3 : set the TOTP as the preferred MFA method
    yield call([Auth, Auth.setPreferredMFA], currentAuthenticatedUser, 'TOTP');
    yield put(verifyTOTP.success({}));
    // TODO this shall be removed once we move 2FA verify token call to BE
    yield put(invalidateUsersList());
  } catch (error) {
    if (error.code === 'EnableSoftwareTokenMFAException') {
      yield put(
        verifyTOTP.failure({
          error,
          noAlert: true,
        })
      );
    } else {
      yield put(verifyTOTP.failure({ error }));
    }
  }
}

export function* setupMobileSaga({ payload }) {
  try {
    const { mobileNumber } = payload;
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );

    // Step 2: update user attribute in Cognito
    yield call([Auth, Auth.updateUserAttributes], currentAuthenticatedUser, {
      phone_number: `+${mobileNumber}`,
    });
    // Step 3: initiate user attribute verification
    const response = yield call(
      [Auth, Auth.verifyUserAttribute],
      currentAuthenticatedUser,
      'phone_number'
    );
    yield put(setupMobile.success(response));
  } catch (error) {
    if (error.code === 'LimitExceededException') {
      yield put(
        setupMobile.failure({
          error,
          noAlert: true,
        })
      );
    } else {
      yield put(setupMobile.failure({ error }));
    }
  }
}

export function* resendMobileVerificationCodeSaga() {
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );
    // Step 2: initiate user attribute verification
    const response = yield call(
      [Auth, Auth.verifyUserAttribute],
      currentAuthenticatedUser,
      'phone_number'
    );
    yield put(resendMobileVerificationCode.success(response));
  } catch (error) {
    if (error.code === 'LimitExceededException') {
      yield put(
        resendMobileVerificationCode.failure({
          error,
          noAlert: true,
        })
      );
    } else {
      yield put(resendMobileVerificationCode.failure({ error }));
    }
  }
}

export function* verifyMobileSaga({ payload }) {
  const { code } = payload;
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );

    yield call(
      [Auth, Auth.verifyUserAttributeSubmit],
      currentAuthenticatedUser,
      'phone_number',
      code
    );

    // Step 3 : set the SMS as the preferred MFA method
    // Step 3.1 : issue (https://github.com/aws-amplify/amplify-js/issues/1226#issuecomment-774097887)
    try {
      yield call(
        [Auth, Auth.setPreferredMFA],
        currentAuthenticatedUser,
        'NOMFA'
      );
    } catch (e) {
      console.debug('ignore this', e);
    }

    // Step 3.2
    const response = yield call(
      [Auth, Auth.setPreferredMFA],
      currentAuthenticatedUser,
      'SMS'
    );
    yield put(verifyMobile.success(response));
    // To invalidate Users List cache in BE. TODO Longer-term fix.
    yield put(invalidateUsersList());
  } catch (error) {
    if (error.code === 'CodeMismatchException') {
      yield put(
        verifyMobile.failure({
          error,
          noAlert: true,
        })
      );
    } else {
      yield put(verifyMobile.failure({ error }));
    }
  }
}

export function* verifyMFASaga({ payload }) {
  const { challengeUser, verificationCode } = payload;
  const { username } = challengeUser;
  const storage = getStorage(storageType);

  try {
    // verify the MFA token
    const response = yield call(
      [Auth, Auth.confirmSignIn],
      challengeUser,
      verificationCode,
      challengeUser.challengeName
    );
    // Step 2 : get user workspaces
    const URL2 = `${envConfig.APIG_URL}tenant/users/${encodeURIComponent(
      username
    )}`; // bff
    const workspaces = yield call(request, URL2, null);
    yield put(getWorkSpaces.success(workspaces.data));

    // if user has only one workspace, set it as the selected one
    const { tenants } = workspaces.data;
    if (tenants.length === 1) {
      yield put(changeWorkSpace.success(tenants[0]));
      storage.setItem(CURRENT_WORK_SPACE, JSON.stringify(tenants[0]));

      // Step 1 : get user details for selected workspace
      const url = `${
        envConfig.APIG_URL
      }user-management/users?username=${encodeURIComponent(username)}`;
      const userDetails = yield call(request, url, null);
      yield put(fetchCurrentUser.success({ user: userDetails.data.users[0] }));
    }

    yield put(logIn.success(response));
  } catch (error) {
    yield put(verifyMFA.failure({ error, challengeUser, noAlert: true }));
  }
}

export function* getPreferredMFASaga() {
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );
    // Step 2 : get preferred MFT type
    const response = yield call(
      [Auth, Auth.getPreferredMFA],
      currentAuthenticatedUser,
      {
        bypassCache: true,
      }
    );
    yield put(getPreferredMFA.success({ preferredMFA: response }));
  } catch (error) {
    yield put(getPreferredMFA.failure({ error }));
  }
}

export function* disableMFASaga() {
  try {
    // verify the session
    yield call(getValidToken);
    // Step 1 : get current authenticated user object
    const currentAuthenticatedUser = yield call(
      [Auth, Auth.currentAuthenticatedUser],
      null
    );
    // Step 2 : set MFA type to 'NOMFA'
    yield call([Auth, Auth.setPreferredMFA], currentAuthenticatedUser, 'NOMFA');
    yield put(getPreferredMFA.trigger());
    yield put(disableMFA.success());
    // To invalidate Users List cache in BE. TODO Longer-term fix.
    yield put(invalidateUsersList());
  } catch (error) {
    yield put(disableMFA.failure({ error }));
  }
}
export function* getHelpJuiceSSOTokenSaga() {
  try {
    const url = `${envConfig.APIG_URL}token/helpjuiceToken`;
    const response = yield call(request, url);
    window.open(
      `https://helpjuice.com/jwt/${envConfig.HELPJUICE_ACCOUNT_NAME}?jwt=${response.data}`,
      '_self'
    );
  } catch (error) {
    yield put(getHelpJuiceSSOToken.failure({ error }));
  }
}

export default function* root() {
  yield all([takeLatest(logIn.TRIGGER, logInSaga)]);
  yield all([takeLatest(ssoLogIn.TRIGGER, SSOLogInSaga)]);
  yield all([takeLatest(logOut.TRIGGER, logOutSaga)]);
  yield all([takeLatest(restoreSession.TRIGGER, restoreSessionSaga)]);
  yield all([takeLatest(resetSession.TRIGGER, restoreSessionSaga)]);
  yield all([takeLatest(completeNewPassword.TRIGGER, CompleteNewPasswordSaga)]);
  yield all([takeLatest(forgotPassword.TRIGGER, forgotPasswordSaga)]);
  yield all([
    takeLatest(forgotPasswordSubmit.TRIGGER, forgotPasswordSubmitSaga),
  ]);
  yield all([takeLatest(setupTOTP.TRIGGER, setupTOTPSaga)]);
  yield all([takeLatest(verifyTOTP.TRIGGER, verifyTOTPSaga)]);
  yield all([takeLatest(setupMobile.TRIGGER, setupMobileSaga)]);
  yield all([takeLatest(verifyMFA.TRIGGER, verifyMFASaga)]);
  yield all([takeLatest(getPreferredMFA.TRIGGER, getPreferredMFASaga)]);
  yield all([takeLatest(disableMFA.TRIGGER, disableMFASaga)]);
  yield all([takeLatest(verifyMobile.TRIGGER, verifyMobileSaga)]);
  yield all([takeLatest(resendMobileMFACode.TRIGGER, resendMobileMFACodeSaga)]);
  yield all([
    takeLatest(
      resendMobileVerificationCode.TRIGGER,
      resendMobileVerificationCodeSaga
    ),
  ]);
  yield all([
    takeLatest(getHelpJuiceSSOToken.TRIGGER, getHelpJuiceSSOTokenSaga),
  ]);
}

// references
// https://github.com/aws-amplify/amplify-js/issues/446  // This will be checked every time a protected route is loaded
// https://github.com/aws-amplify/amplify-js/issues/2560 // Auth.currentSession()
// https://docs.amplify.aws/lib/auth/mfa/q/platform/js/#setup-totp
